'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  History,
  Filter,
  User,
  Sparkles,
  Shield,
  UserCheck,
  CalendarCheck,
  CalendarX,
  CreditCard,
  Tag,
  Settings,
  RefreshCw,
  Search,
  ChevronDown,
  Info,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { SettingsSidebar } from '@/components/dashboard/settings/SettingsSidebar';

interface AuditLogItem {
  id: string;
  actorName: string;
  actorType: 'USER' | 'CLIENT' | 'AURA_AI' | 'SYSTEM';
  action: string;
  entityType: string;
  entityId?: string;
  details?: string;
  createdAt: string;
}

export default function HistorySettingsPage() {
  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedAction, setSelectedAction] = useState<string>('ALL');
  const [selectedActorType, setSelectedActorType] = useState<string>('ALL');
  const [selectedLog, setSelectedLog] = useState<AuditLogItem | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedAction !== 'ALL') params.set('action', selectedAction);
      if (selectedActorType !== 'ALL') params.set('actorType', selectedActorType);

      const res = await fetch(`/api/audit?${params.toString()}`);
      const data = await res.json();
      if (data.success && Array.isArray(data.logs)) {
        setLogs(data.logs);
      }
    } catch (e) {
      console.warn('Error fetching audit logs:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedAction, selectedActorType]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const getActionBadge = (action: string) => {
    switch (action) {
      case 'CREAR_TURNO':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CalendarCheck className="w-3 h-3" />
            Turno Agendado
          </span>
        );
      case 'CREAR_SOBRETURNO':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <CalendarCheck className="w-3 h-3" />
            Sobreturno
          </span>
        );
      case 'ACTUALIZAR_TURNO':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
            <RefreshCw className="w-3 h-3" />
            Turno Modificado
          </span>
        );
      case 'CANCELAR_TURNO':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
            <CalendarX className="w-3 h-3" />
            Turno Cancelado
          </span>
        );
      case 'REGISTRAR_COBRO':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-teal-50 text-teal-700 border border-teal-200">
            <CreditCard className="w-3 h-3" />
            Cobro Registrado
          </span>
        );
      case 'MODIFICAR_PRECIO':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
            <Tag className="w-3 h-3" />
            Cambio de Precio
          </span>
        );
      case 'REGISTRAR_ASISTENCIA':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
            Asistió a Turno
          </span>
        );
      case 'REGISTRAR_INASISTENCIA':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-200">
            <AlertCircle className="w-3 h-3 text-amber-600" />
            No Asistió (No vino)
          </span>
        );
      case 'CREAR_VOUCHER':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
            <Tag className="w-3 h-3" />
            Cupón Creado
          </span>
        );
      case 'AJUSTE_DE_PUNTOS':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-teal-50 text-teal-700 border border-teal-200">
            <Sparkles className="w-3 h-3" />
            Puntos Fidelidad
          </span>
        );
      case 'CAMBIO_DE_PLAN':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
            <Shield className="w-3 h-3" />
            Cambio de Plan
          </span>
        );
      case 'MODIFICAR_CONFIGURACION':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-300">
            <Settings className="w-3 h-3" />
            Configuración
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-50 text-slate-600 border border-slate-200">
            {action}
          </span>
        );
    }
  };

  const getActorBadge = (type: string, name: string) => {
    switch (type) {
      case 'AURA_AI':
        return (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-purple-800 bg-purple-50 px-2 py-0.5 rounded-md border border-purple-200">
            <Sparkles className="w-3 h-3 text-purple-600" />
            {name}
          </span>
        );
      case 'CLIENT':
        return (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
            <UserCheck className="w-3 h-3 text-emerald-600" />
            {name}
          </span>
        );
      case 'USER':
        return (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded-md">
            <User className="w-3 h-3 text-slate-500" />
            {name}
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 bg-slate-50 px-2 py-0.5 rounded-md">
            <Shield className="w-3 h-3 text-slate-400" />
            {name}
          </span>
        );
    }
  };

  const formatDateTime = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('es-AR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const renderDetailsSummary = (detailsStr?: string) => {
    if (!detailsStr) return <span className="text-slate-400 italic">Sin datos adicionales</span>;
    try {
      const parsed = JSON.parse(detailsStr);
      if (parsed.message) return <span>{parsed.message}</span>;
      if (parsed.service) return <span>Servicio: <strong className="font-semibold text-slate-800">{parsed.service}</strong> {parsed.dateStr ? `· ${parsed.dateStr} ${parsed.time || ''}` : ''}</span>;
      if (parsed.total) return <span>Total cobrado: <strong className="font-semibold text-slate-800">${Number(parsed.total).toLocaleString('es-AR')}</strong> ({parsed.channel || 'Efectivo'})</span>;
      if (parsed.serviceName) return <span>{parsed.serviceName}: Nuevo precio <strong className="font-semibold text-slate-800">${Number(parsed.cashPrice || parsed.newPrice).toLocaleString('es-AR')}</strong></span>;
      if (parsed.updatedKeys) return <span>Parámetros actualizados: {parsed.updatedKeys.join(', ')}</span>;
      return <span className="truncate max-w-xs block">{detailsStr}</span>;
    } catch {
      return <span className="truncate max-w-xs block">{detailsStr}</span>;
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Cabecera */}
      <div className="space-y-1">
        <Link
          href="/dashboard/settings"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors mb-2"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Volver a Configuración</span>
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <History className="w-6 h-6 text-emerald-600" />
              Historial de cambios
            </h1>
            <p className="text-sm text-slate-500">
              Trazabilidad completa de turnos, precios, cancelaciones y movimientos realizados por el equipo o clientes.
            </p>
          </div>

          <button
            type="button"
            onClick={fetchLogs}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-2xl bg-white border border-slate-200 text-slate-700 text-xs font-bold hover:bg-slate-50 transition-colors shadow-2xs self-start sm:self-auto cursor-pointer"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-emerald-600' : 'text-slate-500'}`} />
            <span>Actualizar</span>
          </button>
        </div>
      </div>

      {/* Grid con Sidebar Unificado */}
      <div className="grid grid-cols-12 gap-6 items-start">
        {/* Sidebar */}
        <div className="col-span-12 lg:col-span-3">
          <SettingsSidebar currentPath="/dashboard/settings/history" />
        </div>

        {/* Contenido Principal */}
        <div className="col-span-12 lg:col-span-9 space-y-6">
          {/* Filtros */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-5 shadow-sm flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="text-xs font-bold text-slate-700">Filtrar por:</span>
              </div>

              {/* Selector de Acción */}
              <select
                value={selectedAction}
                onChange={(e) => setSelectedAction(e.target.value)}
                className="px-3 py-2 rounded-2xl border border-slate-200 bg-slate-50 text-xs font-semibold text-slate-700 focus:bg-white focus:outline-emerald-600"
              >
                <option value="ALL">Todos los tipos de acción</option>
                <option value="CREAR_TURNO">Turnos agendados</option>
                <option value="REGISTRAR_ASISTENCIA">Asistencia a turnos</option>
                <option value="REGISTRAR_INASISTENCIA">Inasistencias (No vino)</option>
                <option value="ACTUALIZAR_TURNO">Turnos modificados</option>
                <option value="CANCELAR_TURNO">Turnos cancelados</option>
                <option value="REGISTRAR_COBRO">Cobros registrados</option>
                <option value="MODIFICAR_PRECIO">Modificaciones de precio</option>
                <option value="CREAR_VOUCHER">Cupones y Vouchers</option>
                <option value="AJUSTE_DE_PUNTOS">Puntos de fidelización</option>
                <option value="CAMBIO_DE_PLAN">Cambios de suscripción</option>
                <option value="MODIFICAR_CONFIGURACION">Ajustes del negocio</option>
              </select>

              {/* Selector de Actor */}
              <select
                value={selectedActorType}
                onChange={(e) => setSelectedActorType(e.target.value)}
                className="px-3 py-2 rounded-2xl border border-slate-200 bg-slate-50 text-xs font-semibold text-slate-700 focus:bg-white focus:outline-emerald-600"
              >
                <option value="ALL">Todos los actores</option>
                <option value="USER">Usuarios / Equipo</option>
                <option value="CLIENT">Clientes en la web</option>
                <option value="AURA_AI">Aura (Inteligencia Artificial)</option>
                <option value="SYSTEM">Sistema automático</option>
              </select>
            </div>

            <div className="text-xs text-slate-400 font-medium text-right">
              {logs.length} {logs.length === 1 ? 'registro' : 'registros encontrados'}
            </div>
          </div>

          {/* Tabla / Lista de Registros */}
          <div className="bg-white border border-slate-200/90 rounded-3xl shadow-sm overflow-hidden">
            {loading && logs.length === 0 ? (
              <div className="p-12 text-center text-slate-400 text-xs">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-slate-300" />
                Cargando historial de auditoría...
              </div>
            ) : logs.length === 0 ? (
              <div className="p-12 text-center text-slate-500 space-y-2">
                <History className="w-8 h-8 text-slate-300 mx-auto" />
                <p className="text-sm font-semibold">No se encontraron eventos</p>
                <p className="text-xs text-slate-400">
                  Los cambios que realices en turnos, cobros y servicios aparecerán automáticamente en esta bitácora.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50/80 text-slate-400 font-bold uppercase tracking-wider text-[10px] border-b border-slate-100">
                    <tr>
                      <th className="px-5 py-3.5">Fecha y Hora</th>
                      <th className="px-5 py-3.5">Acción</th>
                      <th className="px-5 py-3.5">Actor</th>
                      <th className="px-5 py-3.5">Detalles</th>
                      <th className="px-5 py-3.5 text-right">Info</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-5 py-3.5 text-slate-500 whitespace-nowrap font-medium">
                          {formatDateTime(log.createdAt)}
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          {getActionBadge(log.action)}
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          {getActorBadge(log.actorType, log.actorName)}
                        </td>
                        <td className="px-5 py-3.5 text-slate-600 max-w-xs">
                          {renderDetailsSummary(log.details)}
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <button
                            type="button"
                            onClick={() => setSelectedLog(log)}
                            className="text-xs text-emerald-700 hover:text-emerald-900 font-bold hover:underline cursor-pointer"
                          >
                            Ver
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de Detalle Completo */}
      {selectedLog && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-in fade-in">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl space-y-4 border border-slate-200">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Info className="w-5 h-5 text-emerald-600" />
                <h3 className="text-base font-bold text-slate-900">Detalle del Registro</h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-xs"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3 bg-slate-50 p-3 rounded-2xl">
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Fecha / Hora</span>
                  <span className="font-semibold text-slate-800">{formatDateTime(selectedLog.createdAt)}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Acción</span>
                  <span className="font-semibold text-slate-800">{selectedLog.action}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Actor</span>
                  <span className="font-semibold text-slate-800">{selectedLog.actorName} ({selectedLog.actorType})</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">Entidad Afectada</span>
                  <span className="font-semibold text-slate-800">{selectedLog.entityType} {selectedLog.entityId ? `#${selectedLog.entityId.slice(0, 8)}` : ''}</span>
                </div>
              </div>

              <div>
                <span className="text-slate-400 block text-[10px] uppercase font-bold mb-1.5">Datos Técnicos (JSON)</span>
                <pre className="p-3 bg-slate-950 text-emerald-400 rounded-2xl overflow-x-auto text-[11px] font-mono leading-relaxed">
                  {(() => {
                    try {
                      return JSON.stringify(JSON.parse(selectedLog.details || '{}'), null, 2);
                    } catch {
                      return selectedLog.details || 'Sin datos adicionales';
                    }
                  })()}
                </pre>
              </div>
            </div>

            <div className="pt-2 text-right">
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                className="px-5 py-2 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-colors cursor-pointer"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
